import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { LoginResponse, SystemInfo } from '@nycelogic/nycebuddy-contracts';
import { BuddyApiError } from '@nycelogic/nycebuddy-api-client';
import { Button, Select, TextField } from '@nycelogic/nycebuddy-chat-ui/ui';
import { useTranslation } from '@nycelogic/nycebuddy-chat-ui/i18n';
import { assets, ThemeRoot } from '@nycelogic/nycebuddy-chat-ui';
import { client, prefs, storeLogin } from '../auth/session';

const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY = 2000;

export interface LoginProps {
  /** `/:systemId` route: the picker is replaced by the system name. */
  fixedSystemId?: string;
  onLogin: () => void;
  /** Given = the sign-in is for someone else (the OAuth consent page): the result goes here and is NOT stored as this
   *  tab's session. */
  onSignedIn?: (res: LoginResponse) => void;
  /** The path the picker is narrowed by (default: the page's own); a page that is not an entry point passes `/`. */
  systemsPath?: string;
  /** Replaces the "sign in" line under the logo. */
  prompt?: string;
}

/** System picker (from `GET /systems`), username, password, client, warehouse; remembers the last system. */
export function Login({ fixedSystemId, onLogin, onSignedIn, systemsPath, prompt }: LoginProps) {
  const { t } = useTranslation();
  const [systems, setSystems] = useState<SystemInfo[]>([]);
  const [systemId, setSystemId] = useState(fixedSystemId ?? '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clientCode, setClientCode] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSystems, setLoadingSystems] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  /** The path named one system: it is shown as a label instead of a picker. An entry point serving several (a customer's
   *  prod and test on `…/stark`) keeps the picker, narrowed to those. */
  const [pinned, setPinned] = useState(false);
  /** Seconds left of a sign-in 429 (`rate_limited` per address, `locked_out` per account); 0 = not throttled. */
  const [cooldown, setCooldown] = useState(0);

  const throttled = cooldown > 0;
  useEffect(() => {
    if (!throttled) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [throttled]);

  const fetchSystems = useCallback(
    async (retry = 0): Promise<void> => {
      setLoadingSystems(true);
      setConnectionError(false);
      setError(null);
      try {
        // The address decides the picker: the hostname and the path are sent, so a system published at `*/stark` shows
        // up on `…/stark` and stays off the front page. The path segment is a filter first and a system id second —
        // `/stark` may bring up several systems (prod and test), and only one of them may also be called "stark".
        const path = systemsPath ?? window.location.pathname;
        let list = await client.systems(window.location.hostname, path);
        if (!list.length && window.location.hostname !== 'localhost' && !fixedSystemId)
          list = await client.systems();
        setSystems(list);
        if (fixedSystemId) {
          const match = list.find((s) => s.id.toLowerCase() === fixedSystemId.toLowerCase());
          setPinned(!!match);
          if (match) setSystemId(match.id);
          else if (list.length) {
            // An entry point of its own, not a system id: show the systems it serves and let the user pick.
            const last = prefs.lastSystem();
            setSystemId(list.find((s) => s.id === last)?.id ?? list[0].id);
          } else {
            setNotFound(true);
            setError(t('login.systemNotFound', { system: fixedSystemId }));
          }
        } else {
          const last = prefs.lastSystem();
          setSystemId(list.find((s) => s.id === last)?.id ?? list[0]?.id ?? '');
        }
        setLoadingSystems(false);
      } catch {
        if (retry < MAX_RETRIES - 1) {
          setError(t('login.connectingRetry', { retry: retry + 1, max: MAX_RETRIES }));
          await new Promise((r) => setTimeout(r, INITIAL_RETRY_DELAY * Math.pow(2, retry)));
          return fetchSystems(retry + 1);
        }
        setConnectionError(true);
        setLoadingSystems(false);
        setError(t('login.connectionFailed'));
      }
    },
    [fixedSystemId, systemsPath, t]
  );

  useEffect(() => {
    void fetchSystems(0);
  }, [fetchSystems]);

  const selected = systems.find((s) => s.id === systemId);
  useEffect(() => {
    if (selected?.client && !clientCode) setClientCode(selected.client);
    if (selected?.warehouse && !warehouse) setWarehouse(selected.warehouse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!systemId) {
      setError(t('login.selectSystem'));
      return;
    }
    setLoading(true);
    try {
      const res = await client.login({
        system: systemId,
        username: username.trim(),
        password,
        client: clientCode.trim() || undefined,
        warehouse: warehouse.trim() || undefined,
      });
      setPassword('');
      if (onSignedIn) {
        onSignedIn(res);
        return;
      }
      storeLogin(res);
      onLogin();
    } catch (err) {
      if (err instanceof BuddyApiError && err.isRateLimited) {
        // The server is refusing sign-ins for a while; say why and count the wait down instead of inviting a retry.
        setCooldown(Math.max(1, Math.round(err.retryAfter ?? 60)));
        setError(t(err.code === 'locked_out' ? 'login.lockedOut' : 'login.rateLimited'));
      } else {
        setError(
          err instanceof BuddyApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('login.loginFailed')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const disabled = loadingSystems || notFound || connectionError;
  // The picker groups by the customer that owns the systems, not by the AUTH tenant (which is the installation).
  const owner = (s: SystemInfo) => s.organizationName || s.tenantName || undefined;
  const groups = new Set(systems.map(owner).filter(Boolean));
  return (
    <ThemeRoot theme="light" style={{ background: 'transparent' }}>
      <div className="nb-login">
        <header className="nb-header">
          <img src={assets.logoBlack} alt="nyceBuddy" style={{ width: 200 }} />
          <p className="nb-tagline">{prompt ?? t('login.signInPrompt')}</p>
        </header>
        <form className="nb-login-form" onSubmit={submit}>
          {fixedSystemId && ((pinned && selected) || notFound) ? (
            <label className="nb-field">
              <span>{t('login.system')}</span>
              <div className="nb-login-fixed">{selected?.name ?? `Unknown: ${fixedSystemId}`}</div>
            </label>
          ) : (
            <Select
              label={t('login.system')}
              value={systemId}
              onChange={setSystemId}
              required
              disabled={loadingSystems || connectionError}
              placeholder={
                loadingSystems ? t('login.connectingToServer') : t('login.selectSystemPlaceholder')
              }
              options={systems.map((s) => ({
                value: s.id,
                label: s.environment && s.environment !== 'prod' ? `${s.name} (${s.environment})` : s.name,
                group: groups.size > 1 ? owner(s) : undefined,
              }))}
            />
          )}
          <TextField
            label={t('login.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            disabled={disabled}
          />
          <TextField
            label={t('login.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={disabled}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <TextField
              label={`${t('login.client')} (${t('login.optional')})`}
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value)}
              disabled={disabled}
            />
            <TextField
              label={`${t('login.warehouse')} (${t('login.optional')})`}
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              disabled={disabled}
            />
          </div>
          {error && (
            <div
              className={
                connectionError && !loadingSystems ? 'nb-login-error nb-connection' : 'nb-login-error'
              }
              role={throttled ? 'alert' : undefined}
            >
              {error}
              {throttled && (
                <div data-testid="login-cooldown">{t('login.retryIn', { seconds: cooldown })}</div>
              )}
            </div>
          )}
          {connectionError && !loadingSystems ? (
            <Button variant="secondary" onClick={() => fetchSystems(0)}>
              {t('login.retryConnection')}
            </Button>
          ) : (
            <Button type="submit" variant="secondary" loading={loading} disabled={disabled || throttled}>
              {loadingSystems ? t('login.connecting') : t('login.signIn')}
            </Button>
          )}
        </form>
      </div>
    </ThemeRoot>
  );
}
